import { useState, useCallback, useEffect, useId } from "react";
import { useDropzone } from "react-dropzone";
import styles from "./style.module.scss";
import { Icons } from "assets";

interface CustomProfileUploadProps {
  title?: string;
  description?: string;
  name?: string;
  onChange?: (file: File | null, preview: string | null) => void;
  onRemove?: () => void;
  defaultPreview?: string | null;
  profile_asset?: string | null;
}

const CustomProfileUpload = ({
  title,
  description,
  name,
  onChange,
  onRemove,
  defaultPreview,
  profile_asset,
}: CustomProfileUploadProps) => {
  const isValidImageUrl = (url: string | null | undefined): url is string => {
    if (!url) return false;
    const trimmed = typeof url === "string" ? url.trim() : "";
    return trimmed.length > 0;
  };

  const profileImage = profile_asset || defaultPreview;
  const [preview, setPreview] = useState<string | null>(
    isValidImageUrl(profileImage) ? profileImage : null,
  );
  const inputId = useId();

  useEffect(() => {
    const imageUrl = profile_asset || defaultPreview;
    setPreview(isValidImageUrl(imageUrl) ? imageUrl : null);
  }, [defaultPreview, profile_asset]);

  const getInitials = (fullName?: string) => {
    if (!fullName || !fullName.trim()) return "U";
    const names = fullName
      .trim()
      .split(" ")
      .filter((n) => n.length > 0);
    if (names.length === 0) return "U";
    if (names.length === 1) return names[0][0].toUpperCase();
    return (names[0][0] + names[names.length - 1][0]).toUpperCase();
  };

  const onDrop = useCallback(
    (accepted: File[]) => {
      const file = accepted[0];
      if (!file) return;

      const url = URL.createObjectURL(file);
      setPreview(url);

      if (onChange) onChange(file, url);
    },
    [onChange],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [] },
    multiple: false,
  });

  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    setPreview(null);
    if (onRemove) onRemove();
  };

  return (
    <div className={styles.wrapper}>
      <div
        {...getRootProps()}
        className={`${styles.uploadBox} ${isDragActive ? styles.active : ""}`}
      >
        <input {...getInputProps()} id={inputId} />

        <div className={styles.imageHolder}>
          {preview ? (
            <img src={preview} alt="profile" className={styles.image} />
          ) : (
            <div className={styles.initialsPlaceholder}>
              {getInitials(name || title)}
            </div>
          )}

          <div className={styles.icon}>
            <Icons.Camera />
          </div>

          {preview && onRemove && (
            <button
              type="button"
              onClick={handleRemove}
              className={styles.removeButton}
              aria-label="Remove profile picture"
            >
              <Icons.Cross />
            </button>
          )}
        </div>
      </div>

      {title && <h2 className={styles.title}>{title}</h2>}
      {description && <p className={styles.description}>{description}</p>}
    </div>
  );
};

export default CustomProfileUpload;
